import { useMemo } from 'react'
import { toast } from 'sonner'
import { Star, X } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Combobox, type ComboOption } from '@/components/ui/combobox'
import { CURRENCIES, currencyInfo } from '@/lib/currency'
import { useHousehold } from '@/context/HouseholdContext'
import { useT } from '@/i18n/I18nProvider'
import {
  useUpdateBaseCurrency,
  useUpdateEnabledCurrencies,
  useUsedCurrencies,
} from '@/hooks/useBaseCurrency'

// Configurações → Moeda: lista de moedas HABILITADAS + qual é a PRINCIPAL (base).
export function CurrencySettingsPage() {
  const { baseCurrency, enabledCurrencies, loading } = useHousehold()
  const { t } = useT()
  const setBase = useUpdateBaseCurrency()
  const setEnabled = useUpdateEnabledCurrencies()
  const { data: usedCurrencies } = useUsedCurrencies()

  const busy = loading || setBase.isPending || setEnabled.isPending

  // Moedas do catálogo ainda NÃO habilitadas — para o "Adicionar".
  const addable = useMemo(
    () => CURRENCIES.filter((c) => !enabledCurrencies.includes(c.code)),
    [enabledCurrencies],
  )

  async function makePrincipal(code: string) {
    if (code === baseCurrency) return
    try {
      await setBase.mutateAsync(code)
      toast.success(`Moeda principal alterada para ${code}`, {
        description: 'Todas as transações foram reconvertidas com as taxas históricas.',
      })
    } catch (e) {
      toast.error('Não foi possível trocar a principal', { description: (e as Error).message })
    }
  }

  async function remove(code: string) {
    try {
      await setEnabled.mutateAsync(enabledCurrencies.filter((c) => c !== code))
      toast.success(`Moeda ${code} desabilitada`)
    } catch (e) {
      toast.error('Não foi possível desabilitar', { description: (e as Error).message })
    }
  }

  async function add(code: string) {
    if (!code) return
    try {
      await setEnabled.mutateAsync([...enabledCurrencies, code])
      toast.success(`Moeda ${code} habilitada`)
    } catch (e) {
      toast.error('Não foi possível habilitar', { description: (e as Error).message })
    }
  }

  return (
    <Card className="max-w-lg py-0">
      <CardContent className="space-y-5 p-5">
        <h2 className="font-medium">{t('currency.enabledTitle')}</h2>

        <div className="space-y-2">
          {enabledCurrencies.map((code) => {
            const info = currencyInfo(code)
            const isBase = code === baseCurrency
            const inUse = (usedCurrencies ?? []).includes(code)
            return (
              <div key={code} className="flex items-center justify-between gap-2 rounded-md border p-2.5">
                <div className="min-w-0">
                  <span className="font-medium tabular-nums">{code}</span>
                  <span className="text-muted-foreground"> — {info.name}</span>
                  {isBase && (
                    <Badge variant="secondary" className="text-success ml-2 gap-1">
                      <Star className="size-3 fill-current" /> {t('currency.principal')}
                    </Badge>
                  )}
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  {!isBase && (
                    <Button variant="ghost" size="sm" onClick={() => makePrincipal(code)} disabled={busy}>
                      <Star className="size-4" /> {t('currency.makePrincipal')}
                    </Button>
                  )}
                  {!isBase && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => remove(code)}
                      disabled={busy || inUse}
                      title={inUse ? t('currency.inUse') : t('currency.disable')}
                      aria-label={t('currency.disable')}
                    >
                      <X className="size-4" />
                    </Button>
                  )}
                </div>
              </div>
            )
          })}
        </div>

        <div className="space-y-2 border-t pt-4">
          <h3 className="text-sm font-medium">{t('currency.addTitle')}</h3>
          <Combobox
            className="w-full"
            options={addable.map((c): ComboOption => ({ value: c.code, label: `${c.code} — ${c.name}` }))}
            value=""
            onChange={(code) => void add(code)}
            placeholder={addable.length === 0 ? t('currency.allEnabled') : t('currency.addPlaceholder')}
            searchPlaceholder={t('currency.search')}
            disabled={busy || addable.length === 0}
          />
        </div>

        {(setBase.isPending || setEnabled.isPending) && (
          <div className="text-muted-foreground text-xs">
            {setBase.isPending ? t('currency.reconverting') : t('common.saving')}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
